import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useInView, useReducedMotion } from 'framer-motion';

/**
 * Counts up from 0 to `value` when scrolled into view.
 */
export default function StatCounter({ value, suffix = '', prefix = '', label, duration = 1600 }) {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const reduce = useReducedMotion();
  const [display, setDisplay] = React.useState(0);

  React.useEffect(() => {
    if (!inView) return undefined;
    if (reduce) {
      setDisplay(value);
      return undefined;
    }
    let start = null;
    let frame = null;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (ts) => {
      if (start === null) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setDisplay(Math.round(ease(p) * value));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => frame && cancelAnimationFrame(frame);
  }, [inView, value, duration, reduce]);

  return (
    <Box ref={ref} sx={{ textAlign: 'center' }}>
      <Typography
        sx={{
          fontFamily: '"Sora", "Inter", sans-serif',
          fontWeight: 800,
          lineHeight: 1,
          fontSize: { xs: '2rem', md: '2.6rem' },
        }}
      >
        <Box component="span" className="fx-gradient-text">
          {prefix}
          {display.toLocaleString()}
          {suffix}
        </Box>
      </Typography>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mt: 0.75,
          color: 'text.secondary',
          letterSpacing: 0.6,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
